// The hosted Docs section bundles the repo-root markdown as the single source
// of truth — the same files that live in `docs/` and ship in MRs. `?raw` imports
// inline the content into the JS bundle at build time (see vite.config.ts
// `server.fs.allow` for the dev-server fs grant).
//
// To add a guide: drop the markdown in `docs/`, import it here, add a DocEntry.
import platformGuide from "../../../../docs/PLATFORM-GUIDE.md?raw";
import hubspotGuide from "../../../../docs/integration/hubspot.md?raw";

export interface DocEntry {
  /** URL slug under /docs/<slug>. */
  slug: string;
  /** Sidebar label. */
  title: string;
  /** The repo path this came from (so internal .md links can be resolved). */
  sourcePath: string;
  /** Raw markdown. */
  content: string;
}

export const DOCS: DocEntry[] = [
  {
    slug: "platform",
    title: "Platform Guide",
    sourcePath: "docs/PLATFORM-GUIDE.md",
    content: platformGuide,
  },
  {
    slug: "hubspot",
    title: "HubSpot Integration",
    sourcePath: "docs/integration/hubspot.md",
    content: hubspotGuide,
  },
];

export function getDoc(slug: string): DocEntry | undefined {
  return DOCS.find((d) => d.slug === slug);
}

/**
 * Map an in-document relative markdown link to an in-app /docs route, when it
 * points at another bundled guide. Returns null for links we don't host
 * (runbooks, specs, external URLs) so the caller can render them as plain
 * anchors. `fromPath` is the source path of the doc the link appears in, so a
 * relative href like `./integration/hubspot.md` resolves correctly.
 */
export function resolveDocLink(href: string, fromPath: string): string | null {
  if (!href || /^[a-z]+:/i.test(href) || href.startsWith("#")) return null; // absolute/external/anchor
  // Resolve the href relative to the linking doc's directory.
  const baseDir = fromPath.includes("/") ? fromPath.slice(0, fromPath.lastIndexOf("/")) : "";
  const parts = (baseDir ? `${baseDir}/${href}` : href).split("/");
  const stack: string[] = [];
  for (const part of parts) {
    if (part === "." || part === "") continue;
    if (part === "..") stack.pop();
    else stack.push(part);
  }
  const joined = stack.join("/");
  const hashIndex = joined.indexOf("#");
  const normalized = hashIndex >= 0 ? joined.slice(0, hashIndex) : joined;
  const fragment = hashIndex >= 0 ? joined.slice(hashIndex) : ""; // includes the leading "#"
  const match = DOCS.find((d) => d.sourcePath === normalized);
  // Preserve the section fragment so a cross-link to a heading lands on it.
  return match ? `/docs/${match.slug}${fragment}` : null;
}
